/* ---------------------------------------------------------------------
 * BulkImport.tsx
 * ------------------------------------------------------------------- */

import { useState, useCallback } from "react";
import { ApiError, ImportsService } from "client/openapi";
import {
  OrganizationResponseForAdmins,
  OrganizationDataKeyResponse,
} from "client/openapi";
import Notifications from "util/notifications";
import { APIResponse, PageStatus } from "types";
import { PlusIcon, UploadIcon } from "@radix-ui/react-icons";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";

type TemplateSection = {
  title: string;
  /** Header colour of the section's label cell. */
  fill: string;
  cols: string[];
  examples: string[];
  /** CRM fields: list fields get a drop-down. */
  keys?: OrganizationDataKeyResponse[];
};

/**
 * Write and download an import template: a header row of labelled sections
 * (no merged cells) and one row of examples, with drop-downs for list fields.
 */
async function writeTemplate(sections: TemplateSection[], fileName: string) {
  const patternFill = (argb: string): ExcelJS.FillPattern => ({
    type: "pattern",
    pattern: "solid",
    fgColor: { argb },
  });
  const headerStyle: Partial<ExcelJS.Style> = {
    fill: patternFill("FF366092"),
    font: { color: { argb: "FFFFFFFF" }, bold: true },
    alignment: { horizontal: "center" as const },
  };
  const dataStyle: Partial<ExcelJS.Style> = {
    fill: patternFill("FFFFFFFF"),
    font: { color: { argb: "FF000000" } },
  };

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Import Template");
  const header = sections.flatMap((s) => [s.title, ...s.cols]);
  const headerRow = ws.addRow(header);
  const exampleRow = ws.addRow(sections.flatMap((s) => ["", ...s.examples]));
  headerRow.eachCell((cell) => {
    cell.style = headerStyle;
  });
  exampleRow.eachCell((cell) => {
    cell.style = dataStyle;
  });

  let col = 1; // the section's label column
  sections.forEach((section) => {
    headerRow.getCell(col).style = {
      ...headerStyle,
      fill: patternFill(section.fill),
    };
    exampleRow.getCell(col).style = { fill: patternFill("FFD9D9D9") };
    (section.keys || []).forEach((key, i) => {
      const options =
        key.data_type === "CUSTOM_DATA_TYPE"
          ? key.custom_data_type_key?.values
          : undefined;
      if (!options?.length) return;
      for (let row = 3; row <= 100; row++) {
        ws.getCell(row, col + 1 + i).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [`"${options.map((v) => v.option_name).join(",")}"`],
        };
      }
    });
    col += 1 + section.cols.length;
  });
  header.forEach((c, i) => {
    ws.getColumn(i + 1).width = Math.max(c.length + 2, 15);
  });

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    fileName
  );
}

export default function BulkImport({
  organization,
  studentKeys,
  parentKeys,
  tutorKeys = [],
  mode = "students_parents",
  setStatus,
  setError,
  onSuccess,
}: {
  organization: OrganizationResponseForAdmins;
  studentKeys: OrganizationDataKeyResponse[];
  parentKeys: OrganizationDataKeyResponse[];
  tutorKeys?: OrganizationDataKeyResponse[];
  /** "tutors" fills tutor fields for existing tutors; it never adds tutors. */
  mode?: "students_parents" | "tutors";
  setStatus: (status: PageStatus) => void;
  setError: (error: APIResponse) => void;
  onSuccess?: () => void;
}) {
  /* ------------------------------------------------------------------ */
  /* React state + drag‑and‑drop handlers                               */
  /* ------------------------------------------------------------------ */

  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const [file] = Array.from(e.dataTransfer.files);
    if (file) handleFileSelect(file);
  }, []);

  /* ------------------------------------------------------------------ */
  /* File‑selection helpers                                             */
  /* ------------------------------------------------------------------ */

  const handleFileSelect = (file: File) => {
    const allowed = [
      "text/csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!allowed.includes(file.type)) {
      Notifications.error("Please select a CSV or Excel file");
      return;
    }
    setUploadedFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  /* ------------------------------------------------------------------ */
  /* Download‑template generator (no merged cells)                      */
  /* ------------------------------------------------------------------ */

  /** A CRM block: one column per field, with an example value. */
  const crmSection = (
    title: string,
    fill: string,
    keys: OrganizationDataKeyResponse[]
  ): TemplateSection => ({
    title,
    fill,
    cols: keys.map((k) => k.name),
    examples: keys.map((k) => getExampleValue(k)),
    keys,
  });

  // The backend finds columns by these exact names.
  const downloadTemplate = () =>
    mode === "tutors"
      ? writeTemplate(
          [
            {
              title: "Tutor Info",
              fill: "FFB8A9D9",
              cols: [
                "Tutor First Name",
                "Tutor Last Name",
                "Tutor Email",
                "Tutor Phone Number",
              ],
              examples: ["Jane", "Doe", "jane.doe@example.com", "+1234567891"],
            },
            crmSection("Tutor CRM Data", "FF7E5CD3", tutorKeys),
          ],
          "tutor_import_template.xlsx"
        )
      : writeTemplate(
          [
            {
              title: "Student Info",
              fill: "FFB8A9D9",
              cols: [
                "Student First Name",
                "Student Last Name",
                "Student Phone Number",
                "Student Email",
              ],
              examples: ["John", "Doe", "+1234567890", "john.doe@example.com"],
            },
            {
              title: "Parent Info",
              fill: "FF9B7ED6",
              cols: [
                "Parent First Name",
                "Parent Last Name",
                "Parent Phone Number",
                "Parent Email",
              ],
              examples: ["Jane", "Doe", "+1234567891", "jane.doe@example.com"],
            },
            crmSection("Student CRM Data", "FF7E5CD3", studentKeys),
            crmSection("Parent CRM Data", "FF6139D0", parentKeys),
          ],
          "import_template.xlsx"
        );

  /* ------------------------------------------------------------------ */
  /* Helpers                                                            */
  /* ------------------------------------------------------------------ */

  const getExampleValue = (key: OrganizationDataKeyResponse): string => {
    switch (key.data_type) {
      case "TEXT":
        return "Sample Text";
      case "FLOAT":
        return "42.5";
      case "DATE":
        return "2024-01-15";
      case "BOOLEAN":
        return "true";
      case "CUSTOM_DATA_TYPE":
        if (key.custom_data_type_key?.values.length) {
          return key.custom_data_type_key.values
            .map((v) => v.option_name)
            .join(", ");
        }
        return "Option 1, Option 2";
      default:
        return "";
    }
  };

  /* ------------------------------------------------------------------ */
  /* Upload handler                                                     */
  /* ------------------------------------------------------------------ */

  const handleUpload = async () => {
    if (!uploadedFile) {
      Notifications.error("Please select a file first");
      return;
    }

    setIsUploading(true);
    setStatus(PageStatus.LOADING);

    if (mode === "tutors") {
      try {
        const response = await ImportsService.bulkImportTutors({
          orgId: organization.id,
          formData: { file: uploadedFile },
        });
        const phones = response.tutor_phones_updated ?? 0;
        Notifications.success(
          `Updated ${response.tutors_matched} tutors: ${
            response.tutor_values_created
          } values added, ${response.tutor_values_updated} changed${
            phones > 0
              ? `, ${phones} phone number${phones === 1 ? "" : "s"} saved`
              : ""
          }.`
        );
        const unmatched = response.rows_not_matched;
        if (unmatched.length > 0) {
          // Rows that fit more than one tutor carry their own note; only rows
          // that fit nobody need a new tutor.
          const someMissing = unmatched.some(
            (row) => !row.includes("more than one tutor")
          );
          Notifications.info(
            `Could not match ${unmatched.length} row${
              unmatched.length === 1 ? "" : "s"
            }: ${unmatched.slice(0, 5).join("; ")}${
              unmatched.length > 5 ? "; and more" : ""
            }.${
              someMissing
                ? " Add tutors by inviting them, then import again."
                : ""
            }`
          );
        }
        setUploadedFile(null);
        setStatus(PageStatus.SUCCESS);
        onSuccess?.();
      } catch (err) {
        const e = err as ApiError;
        setStatus(PageStatus.ERROR);
        setError({ message: e.body?.detail ?? e.body?.error });
      } finally {
        setIsUploading(false);
      }
      return;
    }

    try {
      const response = await ImportsService.bulkImportStudentsParents({
        orgId: organization.id,
        formData: { file: uploadedFile },
      });

      Notifications.success(`Import completed successfully!`);
      Notifications.success(
        `Created: ${response.students_created} students, ${response.parents_created} parents.`
      );
      Notifications.success(
        `Updated: ${response.students_updated} students, ${response.parents_updated} parents.`
      );
      Notifications.success(
        `Linked: ${response.relationships_linked} student-parent relationships.`
      );
      Notifications.success(
        `Created ${response.student_values_created} student CRM values and ${response.parent_values_created} parent CRM values.`
      );
      Notifications.success(
        `Updated ${response.student_values_updated} student CRM values and ${response.parent_values_updated} parent CRM values.`
      );

      setUploadedFile(null);
      setStatus(PageStatus.SUCCESS);
      onSuccess?.();
    } catch (err) {
      const e = err as ApiError;
      setStatus(PageStatus.ERROR);
      setError({
        message: e.body.detail,
      });
      console.error(`Error (#${e.status}): ${e.body.detail}`);
    } finally {
      setIsUploading(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* JSX UI                                                             */
  /* ------------------------------------------------------------------ */

  return (
    <div className="dialog-content">
      <div className="bulk-import--dialog-header">
        <h3>Bulk Import</h3>
        <p>
          {mode === "tutors"
            ? "Upload a CSV or Excel file to fill in your tutors' data. Tutors are matched by email, then by name."
            : "Upload a CSV or Excel file to import students, parents, and their data"}
        </p>
        <p className="bulk-import--template-link">
          Want to download an import template?{" "}
          <button
            type="button"
            onClick={downloadTemplate}
            className="bulk-import--template-btn"
          >
            Click here.
          </button>
        </p>
      </div>

      <div
        className={`bulk-import--drop-zone ${
          isDragOver ? "bulk-import--drag-over" : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="bulk-import--content">
          {uploadedFile ? (
            <div className="bulk-import--file-selected">
              <div className="bulk-import--file-info">
                <UploadIcon className="bulk-import--file-icon" />
                <span className="bulk-import--file-name">
                  {uploadedFile.name}
                </span>
                <span className="bulk-import--file-size">
                  ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
                </span>
              </div>
              <button
                className="bulk-import--upload-btn"
                onClick={handleUpload}
                disabled={isUploading}
              >
                {isUploading ? (
                  <div className="bulk-import--spinner">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    <span>Uploading...</span>
                  </div>
                ) : (
                  <span>Submit</span>
                )}
              </button>
            </div>
          ) : (
            <div className="bulk-import--empty-state">
              <div className="bulk-import--upload-icon">
                <PlusIcon />
              </div>
              <div className="bulk-import--text">
                <p className="bulk-import--title">Drag and drop your file</p>
                <p className="bulk-import--subtitle">or</p>
                <label className="bulk-import--file-input-label">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileInput}
                    className="bulk-import--file-input"
                  />
                  <span className="bulk-import--browse-btn">Browse Files</span>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
